import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LegalFooter } from "@/components/LegalFooter";
import { ArrowDownToLine, Bell, CheckCircle2, PawPrint, Share, Smartphone, Sparkles, Wifi } from "lucide-react";

// The Soula Paws app installs straight from the browser (PWA) - full platform functionality,
// home-screen icon, full-screen experience. No app store account needed.
export default function Download() {
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvent(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallEvent(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-30 bg-[hsl(var(--background)/0.92)] backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="download-logo-link">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center"><PawPrint className="w-4 h-4 text-primary-foreground" /></div>
            <span className="font-display text-lg font-semibold">Soula Paws</span>
          </Link>
          <Link to="/register"><Button size="sm" className="rounded-xl" data-testid="download-signup-button">Get started</Button></Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-12 w-full" data-testid="download-page">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge variant="secondary" className="border-0 mb-4 bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))]">Free with your subscription</Badge>
            <h1 className="font-display text-4xl font-semibold leading-tight">Soula Paws in your pocket</h1>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              Install the Soula Paws app straight from your browser — everything the website does, on your home screen: daily check-ins, your pet's Bond Score, tailored rituals and enrichment, AI care specialists, and your care circle.
            </p>
            <div className="mt-5 space-y-2.5">
              {[
                { icon: Sparkles, text: "Identical functionality to the website — it is the full platform" },
                { icon: Bell, text: "In-app alerts, plus email or text notifications (set in your profile)" },
                { icon: Wifi, text: "Loads fast and keeps your last view available even on spotty connections" },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-2.5"><f.icon className="w-4 h-4 text-primary mt-0.5 shrink-0" /><p className="text-sm text-muted-foreground">{f.text}</p></div>
              ))}
            </div>
            {installed ? (
              <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[hsl(var(--success))]" data-testid="app-installed-message"><CheckCircle2 className="w-4 h-4" /> App installed — find Soula Paws on your home screen.</div>
            ) : installEvent ? (
              <Button className="mt-6 rounded-xl" size="lg" onClick={install} data-testid="install-app-button"><ArrowDownToLine className="w-4 h-4 mr-2" /> Install the app now</Button>
            ) : (
              <p className="text-xs text-muted-foreground mt-6">Open this page on your phone and follow the steps below to install.</p>
            )}
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-3xl border border-border shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)] p-6 bg-card">
              <img src="/app-qr.png" alt="QR code - open www.soulapaws.com/download on your phone" className="w-56 h-56" data-testid="download-qr-image" />
            </div>
            <p className="text-xs text-muted-foreground text-center">Scan with your phone's camera to open this page,<br />then install in two taps.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-14">
          <Card className="rounded-2xl" data-testid="install-ios-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3"><Smartphone className="w-5 h-5 text-primary" /><p className="font-semibold">iPhone & iPad</p></div>
              <ol className="space-y-2.5 text-sm text-muted-foreground list-none">
                <li className="flex gap-2"><span className="font-semibold text-foreground">1.</span> Open <span className="font-medium text-foreground">www.soulapaws.com</span> in <span className="font-medium text-foreground">Safari</span></li>
                <li className="flex gap-2"><span className="font-semibold text-foreground">2.</span> Tap the <Share className="w-4 h-4 inline mx-0.5 -mt-0.5" /> <span className="font-medium text-foreground">Share</span> button</li>
                <li className="flex gap-2"><span className="font-semibold text-foreground">3.</span> Choose <span className="font-medium text-foreground">"Add to Home Screen"</span> and tap Add</li>
              </ol>
            </CardContent>
          </Card>
          <Card className="rounded-2xl" data-testid="install-android-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3"><Smartphone className="w-5 h-5 text-primary" /><p className="font-semibold">Android</p></div>
              <ol className="space-y-2.5 text-sm text-muted-foreground list-none">
                <li className="flex gap-2"><span className="font-semibold text-foreground">1.</span> Open <span className="font-medium text-foreground">www.soulapaws.com</span> in <span className="font-medium text-foreground">Chrome</span></li>
                <li className="flex gap-2"><span className="font-semibold text-foreground">2.</span> Tap the <span className="font-medium text-foreground">⋮ menu</span> (or the install banner)</li>
                <li className="flex gap-2"><span className="font-semibold text-foreground">3.</span> Choose <span className="font-medium text-foreground">"Install app"</span> and confirm</li>
              </ol>
            </CardContent>
          </Card>
        </div>
        <p className="text-[11px] text-muted-foreground mt-6 text-center">Soula Paws installs as a secure app directly from the web — always up to date, no app store download required.</p>
      </main>

      <LegalFooter />
    </div>
  );
}
